import CopyableID from 'components/CopyableID';
import { HubertButton } from 'components/HubertUI/HubertButton';
import config from 'config/index';
import { RefreshCw } from 'lucide-react';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { CREATE_MODES, useQuestionEditorContext } from '../context/create-question.context';
import { useAppDispatch, useAppSelector } from 'rtk/query/hooks';
import {
  selectQuestionLanguage,
  selectQuestionMode,
  selectQuestionPreviewRefreshKey,
} from 'rtk/questionEditorSelector';
import { setRefreshPreview } from 'rtk/Question.Editor.Slice';

const QuestionPreviewContainer = ({ className }: { className?: string }) => {
  const { t } = useTranslation('Questionsv2');
  const dispatch = useAppDispatch();
  const { questionId } = useQuestionEditorContext();
  const questionLanguage = useAppSelector((state) => selectQuestionLanguage(state, questionId));
  const mode = useAppSelector((state) => selectQuestionMode(state, questionId));
  const previewRefreshKey = useAppSelector((state) => selectQuestionPreviewRefreshKey(state, questionId));

  // Construct the preview URL
  // Format: VITE_CHAT_INTERVIEW_BASE_URL//question/:questionId/preview?language=:languageCode
  const baseUrl = config.CHAT_INTERVIEW_BASE_URL;
  // Remove trailing slash if present
  const cleanBaseUrl = baseUrl.endsWith('/') ? baseUrl.slice(0, -1) : baseUrl;
  const url = `${cleanBaseUrl}/question/${questionId}/preview?language=${questionLanguage}`;
  const isPreviewBlocked = [...CREATE_MODES].includes(mode);
  const [isLoading, setIsLoading] = useState(isPreviewBlocked ? false : true);
  const previewUrl = isPreviewBlocked ? '' : url;
  useEffect(() => {
    if (!questionId || !config.CHAT_INTERVIEW_BASE_URL) {
      return;
    }
  }, [questionId]);

  const handleIframeLoad = () => {
    setIsLoading(false);
  };

  const handleRefresh = () => {
    setIsLoading(true);
    dispatch(setRefreshPreview({ questionId }));
  };

  if (!questionId) {
    return (
      <div className="bg-background/80 absolute inset-0 z-10 flex items-center justify-center">
        <div className="text-muted-foreground flex items-center gap-2 px-4 text-center text-sm">
          {t('questionPreview.noQuestionId')}
        </div>
      </div>
    );
  }

  return (
    <div className={`flex h-full flex-col ${className ? className : ''}`}>
      {/* Header with refresh button */}
      <div className="bg-muted/50 flex items-center justify-between border-b p-2">
        <span className="text-sm font-medium">{t('questionPreview.heading')}</span>
        <HubertButton
          size="sm"
          variant="secondary"
          onClick={handleRefresh}
          icon={<RefreshCw className="h-4 w-4" />}
          title={t('questionPreview.refreshButton')}
        />
      </div>
      {/* Preview area */}
      <div className="relative flex-1">
        {isPreviewBlocked ? (
          <div className="bg-background/80 absolute inset-0 z-10 flex items-center justify-center">
            <div className="text-muted-foreground flex items-center gap-2 px-4 text-center text-sm">
              {t('questionPreview.notAvailable')}
            </div>
          </div>
        ) : (
          <>
            {isLoading && (
              <div className="bg-background/80 absolute inset-0 z-10 flex items-center justify-center">
                <div className="text-muted-foreground flex items-center gap-2 text-sm">
                  <div className="border-primary h-4 w-4 animate-spin rounded-full border-2 border-t-transparent" />
                  {t('questionPreview.loading')}
                </div>
              </div>
            )}

            {config.CHAT_INTERVIEW_BASE_URL ? (
              <iframe
                key={`preview-${questionId}-${previewRefreshKey}`}
                src={previewUrl}
                className="h-full w-full border-0"
                title={t('questionPreview.heading')}
                onLoad={handleIframeLoad}
                sandbox="allow-scripts allow-same-origin allow-forms allow-popups"
              />
            ) : (
              <div className="bg-background/50 absolute inset-0 flex items-center justify-center">
                DIV
                <div className="text-muted-foreground px-4 text-center text-sm">
                  {!questionId ? t('questionPreview.noQuestionIdForPreview') : t('questionPreview.noBaseUrl')}
                </div>
              </div>
            )}
          </>
        )}
      </div>

      {/* Footer with URL info */}
      {!isPreviewBlocked && previewUrl && (
        <div className="bg-muted/30 text-muted-foreground border-t p-2 text-xs">
          <div className="overflow-x-auto whitespace-nowrap" title={previewUrl}>
            <CopyableID id={previewUrl} url={previewUrl} readOnly={true} showCopyButton={false} />
          </div>
        </div>
      )}
    </div>
  );
};

export default QuestionPreviewContainer;
